import { api } from '@/services/api/AxiosBaseService'
import { createCrudService } from '@/services/api/createCrudService'
import type { SingleResponse, PaginationMeta } from '@/types/api'

// ---- Relatório de Ganhos ----
export interface EarningsReport {
  dateStart: string
  dateEnd: string
  totals: {
    faturamento: number
    pendente: number
    pago: number
    vencido: number
    commissionDue: number
    /** Parceiro (admin não envia): comissões dele, separadas por situação. */
    commissionReceive?: number
    commissionReceivePending?: number
    commissionDuePending?: number
  }
  paymentsByDay: { date: string; total: number; count: number }[]
  byPaymentMethod: { method: string; total: number; count: number }[]
  bySubscriberType: { type: string; total: number; count: number }[]
  byCompanyType: { type: string; total: number; count: number }[]
}

// ---- Relatório de Comissionamento ----
export interface CommissionsReport {
  dateStart: string
  dateEnd: string
  byType: { type: string; total: number; pendente: number; consolidado: number; count: number }[]
}
/** Comissionamento agrupado por comissionado (uma linha por pessoa/papel). */
export interface CommissionTransaction {
  id: string
  userId: string
  type: string
  userName: string
  total: number
  pendente: number
  consolidado: number
  count: number
}

/** Uma linha de detalhamento de comissão (fatura a fatura) para o drawer. */
export interface CommissionDetailItem {
  revenueId: string
  code: string
  client: string
  clientType: 'subscriber' | 'company' | null
  recipientName: string | null
  recipientType: string | null
  invoiceAmount: number
  base: number
  percentage: number
  amount: number
  dueDate: string | null
  paidDate: string | null
  status: string
}
export interface CommissionDetail {
  items: CommissionDetailItem[]
  total: number
}

export interface Revenue {
  id: string
  code: string
  subscriberId: string | null
  companyId: string | null
  amount: number
  amountInCents: number
  dueDate: string | null
  paidDate: string | null
  bankSlipUrl: string | null
  method: string | null
  gateway: string | null
  /** Id da cobrança no Asaas. Preenchido = fatura controlada pelo gateway. */
  externalId: string | null
  /**
   * A cobrança existe MESMO no Asaas (verificado no gateway).
   * null = nunca verificada. É o que a badge Asaas/Sistema usa — `gateway` e
   * `externalId` não são confiáveis para isso.
   */
  hasAsaasCharge: boolean | null
  asaasCheckedAt: string | null
  /** Nº de lançamentos do extrato. Só vem na listagem (withCount). */
  statementsCount?: number
  status: string
  subscriber?: { id: string; name: string; cpf: string | null } | null
  company?: { id: string; name: string } | null
  statements?: RevenueStatement[]
  commissions?: RevenueCommission[]
  formatted?: {
    amount?: string
    dueDate?: string
    paidDate?: string
    createdAt?: string
    subtotal?: string | null
    discount?: string | null
  }
}

export interface RevenueStatement {
  id: string
  subscriberId: string | null
  amount: number
  planAmount: number
  discountAmount: number
  couponCode: string | null
  couponInstallments: number | null
  sequence: number
  description: string | null
  subscriber?: { id: string; name: string } | null
  /** Assinante existe, mas foi excluído depois desta fatura. */
  subscriberRemoved?: boolean
  /** Sem vínculo com assinante — o valor da linha continua valendo. */
  subscriberMissing?: boolean
  /** Nome guardado na importação quando o assinante não veio junto. */
  subscriberFallbackName?: string | null
  formatted?: { amount?: string; planAmount?: string; discountAmount?: string }
}

export interface RevenueCommission {
  id: string
  userId: string
  type: string
  amount: number
  isFirstLevel: boolean
  user?: { id: string; name: string } | null
  formatted?: { amount?: string }
}

export const RevenuesService = {
  ...createCrudService<Revenue>('/financial/revenues'),

  async totalPaidCurrentMonth() {
    const r = await api.get<SingleResponse<{ total: number }>>('/financial/revenues/total-paid/current-month')
    return r.data.data.total
  },
  async totalCommissions() {
    const r = await api.get<SingleResponse<{ total: number }>>('/financial/revenues/total-commissions')
    return r.data.data.total
  },
  async nextMonthPreview() {
    const r = await api.get<SingleResponse<{ subscribers: number; forecast: number }>>('/financial/revenues/next-month-preview')
    return r.data.data
  },
  async deleteStatement(statementId: string) {
    await api.delete(`/financial/revenues/statements/${statementId}`)
  },
  /** Colaboradores da empresa (ativos, com plano) que ainda não estão no extrato. */
  async addableCollaborators(revenueId: string): Promise<{ id: string; name: string; planName: string | null }[]> {
    const r = await api.get<{ data: { id: string; name: string; planName: string | null }[] }>(`/financial/revenues/${revenueId}/addable-collaborators`)
    return r.data.data
  },
  /** Adiciona um colaborador ao extrato de uma fatura de empresa (PENDENTE). */
  async addStatement(revenueId: string, subscriberId: string) {
    const r = await api.post<SingleResponse<Revenue>>(`/financial/revenues/${revenueId}/statements`, { subscriber_id: subscriberId })
    return r.data.data
  },
  async updateBankSlip(id: string, dueDate: string) {
    const r = await api.post<SingleResponse<Revenue>>(`/financial/revenues/${id}/update-bank-slip`, { due_date: dueDate })
    return r.data.data
  },
  async markAsPaid(id: string, payload: { date: string; paymentMethod: string }) {
    const r = await api.post<SingleResponse<Revenue>>(`/financial/revenues/${id}/mark-as-paid`, payload)
    return r.data.data
  },
  async markAsWaived(id: string) {
    const r = await api.post<SingleResponse<Revenue>>(`/financial/revenues/${id}/mark-as-waived`, {})
    return r.data.data
  },
  /** Reaplica os cupons dos assinantes ao extrato/total (não altera o Asaas). */
  async reapplyCoupons(id: string) {
    const r = await api.post<SingleResponse<Revenue>>(`/financial/revenues/${id}/reapply-coupons`, {})
    return r.data.data
  },
  async markAsCancelled(id: string) {
    const r = await api.post<SingleResponse<Revenue>>(`/financial/revenues/${id}/mark-as-cancelled`, {})
    return r.data.data
  },
  async reportsEarnings(from: string, to: string) {
    const r = await api.get<SingleResponse<EarningsReport>>('/financial/revenues/reports/earnings', {
      params: { date_start: from, date_end: to },
    })
    return r.data.data
  },
  async reportsCommissions(from: string, to: string) {
    const r = await api.get<SingleResponse<CommissionsReport>>('/financial/revenues/reports/commissions', {
      params: { date_start: from, date_end: to },
    })
    return r.data.data
  },
  async reportsCommissionTransactions(params: { from: string; to: string; type?: string; q?: string; page?: number; size?: number }) {
    const r = await api.get<{ data: CommissionTransaction[]; meta: PaginationMeta }>(
      '/financial/revenues/reports/commission-transactions',
      {
        params: {
          date_start: params.from,
          date_end: params.to,
          ...(params.type ? { type: params.type } : {}),
          ...(params.q ? { q: params.q } : {}),
          page: params.page ?? 1,
          size: params.size ?? 25,
        },
      },
    )
    return r.data
  },
  async reportsCommissionDetail(params: { from: string; to: string; userId: string; type?: string; status: 'consolidado' | 'pendente' }) {
    const r = await api.get<SingleResponse<CommissionDetail>>('/financial/revenues/reports/commission-detail', {
      params: {
        date_start: params.from, date_end: params.to,
        user_id: params.userId,
        ...(params.type ? { type: params.type } : {}),
        status: params.status,
      },
    })
    return r.data.data
  },
  async summary(dateStart?: string, dateEnd?: string) {
    const params: Record<string, string> = {}
    if (dateStart) params.date_start = dateStart
    if (dateEnd)   params.date_end   = dateEnd
    const r = await api.get('/financial/revenues/summary', { params })
    return r.data.data as {
      dateStart: string
      dateEnd: string
      byStatus: { status: string; count: number; total: number }[]
      totals: { count: number; total: number }
    }
  },
}
